import axios from 'axios';

// Create axios instance with base URL from environment variables
const api = axios.create({
  // Remove the /api suffix from the base URL since we're including it in the request paths
  baseURL: process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false, // Set to true if using cookies for authentication
});

// Log configuration for debugging
console.log('API Base URL:', process.env.REACT_APP_API_URL);

// Add JWT token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log(`Adding auth token to ${config.method.toUpperCase()} ${config.url}`);
      
      // Debug token information
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const expiryDate = new Date(payload.exp * 1000);
          const isExpired = expiryDate < new Date();
          console.log(`Token expiry: ${expiryDate.toLocaleString()}, Expired: ${isExpired}`);
          
          // If token is expired, we could trigger a refresh here
          if (isExpired) {
            console.warn('Token is expired! Attempting to refresh...');
            // Try to refresh the token - this is a placeholder, implement actual refresh logic
            // For now, we'll just log the warning
            // You could redirect to login page or trigger a refresh token API call here
          }
        }
      } catch (e) {
        console.error('Error parsing JWT token:', e);
      }
    } else {
      console.warn(`No auth token available for ${config.method.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Create a custom event for session expiration
export const SESSION_EXPIRED_EVENT = 'session_expired';

// Add response interceptor for handling common errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    console.log(`API Response [${response.config.method.toUpperCase()} ${response.config.url}]:`, response.data);
    return response;
  },
  (error) => {
    // Log API errors for debugging
    console.error(`API Error [${error.config?.method?.toUpperCase()} ${error.config?.url}]:`, {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      console.error('Authentication error:', error.response.data);
      
      // Get current path to redirect back after login
      const currentPath = window.location.pathname;
      
      // Clear token and user data if unauthorized
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Store the current path to redirect back after login
      if (currentPath !== '/login' && !currentPath.includes('/admin/login')) {
        localStorage.setItem('redirectAfterLogin', currentPath);
      }
      
      // Dispatch a custom event to notify the app about session expiration
      const sessionExpiredEvent = new CustomEvent(SESSION_EXPIRED_EVENT, {
        detail: {
          message: error.response?.data?.message || 'Your session has expired. Please log in again.',
          redirectPath: currentPath
        }
      });
      window.dispatchEvent(sessionExpiredEvent);
      
      // If this is an API call from an admin page, redirect to admin login
      if (currentPath.includes('/admin')) {
        window.location.href = '/admin/login';
      } else if (!currentPath.includes('/login')) {
        // Only redirect if not already on the login page
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
